import { Brain } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "react-i18next";
import { AnalysisErrorBoundary } from "@/components/AnalysisErrorBoundary";
import { toNumber, toText, toTextList } from "@/lib/analysisSafe";

interface CandidateAnalysisTabProps {
	candidateId: string;
	analysis: any;
}

const CandidateAnalysisContent = ({ candidateId, analysis }: CandidateAnalysisTabProps) => {
	const { t } = useTranslation();

	// Safe helper to extract value from analysis field
	const safeExtractValue = (field: any, defaultValue: any = null) => {
		try {
			if (!field || typeof field !== 'object') return defaultValue;
			return field["Extracted Value"] !== undefined ? field["Extracted Value"] : defaultValue;
		} catch (error) {
			console.warn('Error extracting value from field:', error);
			return defaultValue;
		}
	};

	// Safe helper to extract explanation from analysis field
	const safeExtractExplanation = (field: any, defaultExplanation: string = '') => {
		try {
			if (!field || typeof field !== 'object') return defaultExplanation;
			return toText(field.Explanation) || defaultExplanation;
		} catch (error) {
			console.warn('Error extracting explanation from field:', error);
			return defaultExplanation;
		}
	};

	if (!analysis || typeof analysis !== 'object' || Object.keys(analysis).length === 0) {
		return (
			<Card className="mb-6">
				<CardHeader>
					<CardTitle className="text-xl flex items-center">
						<Brain className="mr-2 h-5 w-5 text-blue-500" />
						{t("candidates.analysis.title", "Candidate Analysis")}
					</CardTitle>
					<CardDescription>
						{t("candidates.analysis.noAI", "No AI analysis available for this candidate yet.")}
					</CardDescription>
				</CardHeader>
			</Card>
		);
	}

	// Helper: Render metric with progress bar
	const renderMetric = (title: string, value: any, explanation: string) => {
		try {
			if (value === undefined || value === null) return null;
			
			const safeTitle = title || 'Unknown Metric';
			const safeExplanation = toText(explanation);
			const numericValue = toNumber(value);
			
			return (
				<div className="space-y-1.5">
					<div className="flex justify-between text-sm">
						<span>{safeTitle}</span>
						{numericValue !== null && <span>{Math.round(numericValue * 100)}%</span>}
					</div>
					{numericValue !== null && (
						<Progress value={Math.min(Math.max(numericValue * 100, 0), 100)} className="h-2" />
					)}
					{safeExplanation && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{safeExplanation}</p>}
				</div>
			);
		} catch (error) {
			console.warn('Error rendering metric:', error);
			return null;
		}
	};

	// Helper: Render list as badges
	const renderList = (title: string, items: unknown, explanation: string) => {
		try {
			const safeItems = toTextList(items);
			
			const safeTitle = title || 'Items';
			const safeExplanation = toText(explanation);
			
			if (safeItems.length === 0) return null;
			
			return (
				<div className="space-y-2">
					<h4 className="font-medium text-gray-900 dark:text-gray-100">{safeTitle}</h4>
					<div className="flex flex-wrap gap-1.5">
						{safeItems.map((item, index) => (
							<Badge key={index} variant="secondary">{item}</Badge>
						))}
					</div>
					{safeExplanation && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{safeExplanation}</p>}
				</div>
			);
		} catch (error) {
			console.warn('Error rendering list:', error);
			return null;
		}
	};

	// Helper: Render object as table
	const renderObjectTable = (title: string, obj: Record<string, any>, explanation: string) => {
		try {
			if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
			
			const entries = Object.entries(obj).filter(([k, v]) => 
				k !== null && k !== undefined && k !== '' && 
				v !== null && v !== undefined && v !== ''
			);
			
			if (entries.length === 0) return null;
			
			const safeTitle = title || 'Data';
			const safeExplanation = toText(explanation);

			return (
				<div className="space-y-2">
					<h4 className="font-medium text-gray-900 dark:text-gray-100">{safeTitle}</h4>
					<div className="overflow-x-auto">
						<table className="min-w-[200px] text-sm border rounded">
							<tbody>
								{entries.map(([k, v], idx) => (
									<tr key={idx} className="border-b last:border-b-0">
										<td className="px-2 py-1 font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">{toText(k)}</td>
										<td className="px-2 py-1 text-gray-600 dark:text-gray-400 whitespace-nowrap">{toText(v)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					{safeExplanation && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{safeExplanation}</p>}
				</div>
			);
		} catch (error) {
			console.warn('Error rendering object table:', error);
			return null;
		}
	};

	// Helper: Render roles/jobs as table
	const renderRolesTable = (title: string, roles: any[], explanation: string) => {
		try {
			if (!roles || !Array.isArray(roles) || roles.length === 0) return null;
			
			const safeRoles = roles.filter(role => role && typeof role === 'object');
			if (safeRoles.length === 0) return null;
			
			const safeTitle = title || 'Roles';
			const safeExplanation = toText(explanation);
			
			return (
				<div className="space-y-2">
					<h4 className="font-medium text-gray-900 dark:text-gray-100">{safeTitle}</h4>
					<div className="overflow-x-auto">
						<table className="min-w-[300px] text-sm border rounded">
							<thead>
								<tr className="bg-gray-50 dark:bg-gray-800">
									<th className="px-2 py-1 text-left">Role</th>
									<th className="px-2 py-1 text-left">Company</th>
									<th className="px-2 py-1 text-left">Dates</th>
								</tr>
							</thead>
							<tbody>
								{safeRoles.map((r, idx) => (
									<tr key={idx} className="border-b last:border-b-0">
										<td className="px-2 py-1 whitespace-nowrap">{toText(r.role || r.title) || 'N/A'}</td>
										<td className="px-2 py-1 whitespace-nowrap">{toText(r.company) || 'N/A'}</td>
										<td className="px-2 py-1 whitespace-nowrap">{toText(r.dates || r.date) || 'N/A'}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					{safeExplanation && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{safeExplanation}</p>}
				</div>
			);
		} catch (error) {
			console.warn('Error rendering roles table:', error);
			return null;
		}
	};

	// Helper: Safe render of hidden risks section
	const renderHiddenRisks = () => {
		try {
			const missedInfoRisk = analysis.missed_information_risk;
			if (!missedInfoRisk || typeof missedInfoRisk !== 'object') return null;
			
			const explanation = safeExtractExplanation(missedInfoRisk);
			const value = safeExtractValue(missedInfoRisk);
			
			if (!explanation && !value) return null;
			
			return (
				<div className="mt-6">
					<h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">{t("candidates.analysis.hiddenRisks", "Hidden Risks")}</h4>
					<div className="bg-amber-50 p-4 rounded-sm">
						{explanation && <p className="text-sm text-amber-800 mb-2">{explanation}</p>}
						{value && <p className="text-sm text-amber-700">{toText(value)}</p>}
					</div>
				</div>
			);
		} catch (error) {
			console.warn('Error rendering hidden risks:', error);
			return null;
		}
	};

	//console.log('analysis', analysis)

	// Wrap the entire render in a try-catch to prevent complete crashes
	try {
		return (
			<div className="space-y-6">
				<Card className="mb-6">
					<CardHeader>
						<CardTitle className="text-xl flex items-center">
							<Brain className="mr-2 h-5 w-5 text-blue-500" />
							{t("candidates.analysis.aiTitle", "AI Candidate Analysis")}
						</CardTitle>
						<CardDescription>
							{t("candidates.analysis.insights", "Insights extracted from the candidate profile by AI")}
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-6">
						{/* Skills & Technologies */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{renderList(
								t("candidates.analysis.skills", "Skills Possessed"),
								safeExtractValue(analysis.skills_possessed, []),
								safeExtractExplanation(analysis.skills_possessed)
							)}
							{renderList(
								t("candidates.analysis.technologies", "Technologies Familiar"),
								safeExtractValue(analysis.technologies_familiar, []),
								safeExtractExplanation(analysis.technologies_familiar)
							)}
							{renderList(
								t("candidates.analysis.certifications", "Certifications Held"),
								safeExtractValue(analysis.certifications_held, []),
								safeExtractExplanation(analysis.certifications_held)
							)}
						</div>

						<Separator />

						{/* Work Environment */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div className="space-y-4">
								{renderMetric(
									t("candidates.analysis.workLifeBalance", "Work-Life Balance Needs"),
									safeExtractValue(analysis.work_life_balance_needs),
									safeExtractExplanation(analysis.work_life_balance_needs)
								)}
								{renderMetric(
									t("candidates.analysis.remoteWork", "Remote Work Preference"),
									safeExtractValue(analysis.remote_work_preference),
									safeExtractExplanation(analysis.remote_work_preference)
								)}
								{renderMetric(
									t("candidates.analysis.hybridWork", "Hybrid Work Flexibility"),
									safeExtractValue(analysis.hybrid_work_flexibility),
									safeExtractExplanation(analysis.hybrid_work_flexibility)
								)}
								{renderMetric(
									t("candidates.analysis.asynchronousWork", "Asynchronous Work Preference"),
									safeExtractValue(analysis.asynchronous_work_preference),
									safeExtractExplanation(analysis.asynchronous_work_preference)
								)}
							</div>
							<div className="space-y-4">
								{renderMetric(
									t("candidates.analysis.recognition", "Recognition Need"),
									safeExtractValue(analysis.recognition_need),
									safeExtractExplanation(analysis.recognition_need)
								)}
								{renderMetric(
									t("candidates.analysis.hierarchy", "Hierarchy Preference"),
									safeExtractValue(analysis.hierarchy_preference),
									safeExtractExplanation(analysis.hierarchy_preference)
								)}
								{renderMetric(
									t("candidates.analysis.trust", "Trust Preference"),
									safeExtractValue(analysis.trust_preference),
									safeExtractExplanation(analysis.trust_preference)
								)}
								{renderMetric(
									t("candidates.analysis.collaboration", "Collaboration Preference"),
									safeExtractValue(analysis.collaboration_preference),
									safeExtractExplanation(analysis.collaboration_preference)
								)}
							</div>
						</div>

						<Separator />

						{/* Work Style */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div className="space-y-4">
								{renderMetric(
									t("candidates.analysis.communication", "Communication Style"),
									safeExtractValue(analysis.communication_style),
									safeExtractExplanation(analysis.communication_style)
								)}
								{renderMetric(
									t("candidates.analysis.decisionMaking", "Decision Making Style"),
									safeExtractValue(analysis.decision_making_style),
									safeExtractExplanation(analysis.decision_making_style)
								)}
								{renderMetric(
									t("candidates.analysis.meetingTolerance", "Meeting Tolerance"),
									safeExtractValue(analysis.meeting_tolerance),
									safeExtractExplanation(analysis.meeting_tolerance)
								)}
								{renderMetric(
									t("candidates.analysis.overtimeAcceptance", "Overtime Acceptance"),
									safeExtractValue(analysis.overtime_acceptance),
									safeExtractExplanation(analysis.overtime_acceptance)
								)}
							</div>
							<div className="space-y-4">
								{renderMetric(
									t("candidates.analysis.stressTolerance", "Stress Tolerance"),
									safeExtractValue(analysis.stress_tolerance),
									safeExtractExplanation(analysis.stress_tolerance)
								)}
								{renderMetric(
									t("candidates.analysis.urgencyTolerance", "Urgency Tolerance"),
									safeExtractValue(analysis.urgency_tolerance),
									safeExtractExplanation(analysis.urgency_tolerance)
								)}
								{renderMetric(
									t("candidates.analysis.availabilityFlexibility", "Availability Flexibility"),
									safeExtractValue(analysis.availability_flexibility),
									safeExtractExplanation(analysis.availability_flexibility)
								)}
								{renderMetric(
									t("candidates.analysis.performanceEvaluation", "Performance Evaluation Style"),
									safeExtractValue(analysis.performance_evaluation_style),
									safeExtractExplanation(analysis.performance_evaluation_style)
								)}
							</div>
						</div>

						<Separator />

						{/* Career & Compensation */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div className="space-y-4">
								{renderMetric(
									t("candidates.analysis.educationLevel", "Education Level"),
									safeExtractValue(analysis.education_level),
									safeExtractExplanation(analysis.education_level)
								)}
								{renderMetric(
									t("candidates.analysis.yearsExperienceTotal", "Years of Experience (Total)"),
									(() => {
										const value = safeExtractValue(analysis.years_experience_total);
										return value !== null ? `${value} Years` : null;
									})(),
									safeExtractExplanation(analysis.years_experience_total)
								)}
								{renderObjectTable(
									t("candidates.analysis.yearsExperienceJob", "Years of Experience by Job"),
									safeExtractValue(analysis.years_experience_job, {}),
									safeExtractExplanation(analysis.years_experience_job)
								)}
								{renderMetric(
									t("candidates.analysis.promotionExpectation", "Promotion Expectation"),
									safeExtractValue(analysis.promotion_expectation),
									safeExtractExplanation(analysis.promotion_expectation)
								)}
								{renderMetric(
									t("candidates.analysis.riskTolerance", "Risk Tolerance"),
									safeExtractValue(analysis.risk_tolerance),
									safeExtractExplanation(analysis.risk_tolerance)
								)}
							</div>
							<div className="space-y-4">
								{renderMetric(
									t("candidates.analysis.salaryExpectation", "Salary Expectation"),
									safeExtractValue(analysis.salary_expectation),
									safeExtractExplanation(analysis.salary_expectation)
								)}
								{renderMetric(
									t("candidates.analysis.bonusDependency", "Bonus Dependency"),
									safeExtractValue(analysis.bonus_dependency),
									safeExtractExplanation(analysis.bonus_dependency)
								)}
							</div>
						</div>

						<Separator />

						{/* Innovation & Adaptation */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div className="space-y-4">
								{renderMetric(
									t("candidates.analysis.technologyAdaptation", "Technology Adaptation"),
									safeExtractValue(analysis.technology_adaptation),
									safeExtractExplanation(analysis.technology_adaptation)
								)}
								{renderMetric(
									t("candidates.analysis.innovationFlexibility", "Innovation Flexibility"),
									safeExtractValue(analysis.innovation_flexibility),
									safeExtractExplanation(analysis.innovation_flexibility)
								)}
								{renderMetric(
									t("candidates.analysis.changeResistance", "Change Resistance"),
									safeExtractValue(analysis.change_resistance),
									safeExtractExplanation(analysis.change_resistance)
								)}
							</div>
						</div>

						<Separator />

						{/* Roles/Jobs Table */}
						{renderRolesTable(
							t("candidates.analysis.rolesJobs", "Roles, Jobs, Companies & Dates"),
							safeExtractValue(analysis.roles_jobs_companies_dates, []),
							safeExtractExplanation(analysis.roles_jobs_companies_dates)
						)}

						{/* Hidden Risks */}
						{renderHiddenRisks()}
					</CardContent>
				</Card>
			</div>
		);
	} catch (error) {
		console.error('Critical error in CandidateAnalysisTab:', error);
		return (
			<Card className="mb-6">
				<CardHeader>
					<CardTitle className="text-xl flex items-center">
						<Brain className="mr-2 h-5 w-5 text-red-500" />
						{t("candidates.analysis.error", "Analysis Error")}
					</CardTitle>
					<CardDescription>
						{t("candidates.analysis.errorMessage", "There was an error displaying the candidate analysis. Please try refreshing the page.")}
					</CardDescription>
				</CardHeader>
			</Card>
		);
	}
};

export const CandidateAnalysisTab = (props: CandidateAnalysisTabProps) => {
	const { t } = useTranslation();

	return (
		<AnalysisErrorBoundary
			fallback={
				<Card className="mb-6">
					<CardHeader>
						<CardTitle className="text-xl flex items-center">
							<Brain className="mr-2 h-5 w-5 text-red-500" />
							{t("candidates.analysis.error", "Analysis Error")}
						</CardTitle>
						<CardDescription>
							{t("candidates.analysis.errorMessage", "There was an error displaying the candidate analysis. Please try refreshing the page.")}
						</CardDescription>
					</CardHeader>
				</Card>
			}
		>
			<CandidateAnalysisContent {...props} />
		</AnalysisErrorBoundary>
	);
};